// ===========================================
// Edit an enquiry's details (enquiries plan §5.5, §7)
// ===========================================
// PATCH /:id: the contact, the children, temperature (when the school uses it), availability, the
// marketing source and the newsletter opt-in. Never the stage, owner or next action: those have
// their own doors. The enquiry keeps an email OR a phone. Children keep their ids, so a child
// enrolled as a student stays linked (and cannot be removed). Only what changed is sent.

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { Enquiry, EnquiryTemperature, UpdateEnquiryData } from '../../../api/enquiry.api';
import { MANUAL_ENQUIRY_LIMITS } from '../../../api/enquiry.api';
import { apiErrorMessage, useEnquiryConfig, useUpdateEnquiry } from '../../../hooks/useEnquiries';
import ChildrenEditor from './ChildrenEditor';
import { ageError, childRowFrom, childrenPayload, type ChildRow } from './childRows';
import EnquiryOptionSelect from './EnquiryOptionSelect';
import { TEMPERATURE_LABELS } from './enquiryUi';

const L = MANUAL_ENQUIRY_LIMITS;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface EditEnquiryDetailsDialogProps {
  enquiry: Enquiry;
  open: boolean;
  onClose: () => void;
}

export default function EditEnquiryDetailsDialog({ enquiry, open, onClose }: EditEnquiryDetailsDialogProps) {
  const { data: config } = useEnquiryConfig();
  const temperatureEnabled = config?.settings.temperatureEnabled === true;
  const update = useUpdateEnquiry({ quietErrors: true });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [children, setChildren] = useState<ChildRow[]>([]);
  const [temperature, setTemperature] = useState<'' | EnquiryTemperature>('');
  const [availability, setAvailability] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFirstName(enquiry.firstName);
    setLastName(enquiry.lastName);
    setEmail(enquiry.email ?? '');
    setPhone(enquiry.phone ?? '');
    setChildren(enquiry.Children.map(childRowFrom));
    setTemperature(enquiry.temperature ?? '');
    setAvailability(enquiry.availability ?? '');
    setSourceId(enquiry.enquirySourceId ?? '');
    setOptIn(enquiry.newsletterOptIn === true);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enquiry.id]);

  const emailBad = email.trim() !== '' && !EMAIL_SHAPE.test(email.trim());
  const noContact = email.trim() === '' && !/\d/.test(phone);
  const valid = firstName.trim() !== '' && !emailBad && !noContact && !children.some((c) => ageError(c.age));

  const handleSave = () => {
    if (!valid) return;
    setError(null);
    const data: UpdateEnquiryData = {};
    if (firstName.trim() !== enquiry.firstName) data.firstName = firstName.trim();
    if (lastName.trim() !== enquiry.lastName) data.lastName = lastName.trim();
    if (email.trim() !== (enquiry.email ?? '')) data.email = email.trim() || null;
    if (phone.trim() !== (enquiry.phone ?? '')) data.phone = phone.trim() || null;
    if (temperatureEnabled && (temperature || null) !== enquiry.temperature) data.temperature = temperature || null;
    if (availability.trim() !== (enquiry.availability ?? '')) data.availability = availability.trim() || null;
    if (sourceId && sourceId !== enquiry.enquirySourceId) data.enquirySourceId = sourceId;
    if (optIn !== (enquiry.newsletterOptIn === true)) data.newsletterOptIn = optIn;
    // The full list (the server keeps ids, so an unchanged child is not rewritten).
    data.children = childrenPayload(children);

    update.mutate(
      { id: enquiry.id, data },
      {
        onSuccess: () => onClose(),
        onError: (e) => setError(apiErrorMessage(e, 'Failed to save the details')),
      },
    );
  };

  return (
    <Dialog open={open} onClose={update.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit details</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              label="First name"
              required
              fullWidth
              size="small"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              inputProps={{ maxLength: L.name }}
              error={firstName.trim() === ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last name"
              fullWidth
              size="small"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              inputProps={{ maxLength: L.name }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailBad || noContact}
              helperText={emailBad ? 'Invalid email address' : noContact ? 'Give an email address or a phone number' : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              type="tel"
              fullWidth
              size="small"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputProps={{ maxLength: L.phone }}
            />
          </Grid>
          <Grid item xs={12}>
            <ChildrenEditor rows={children} onChange={setChildren} disabled={update.isPending} />
          </Grid>
          {temperatureEnabled && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-enquiry-temperature-label">Temperature</InputLabel>
                <Select
                  labelId="edit-enquiry-temperature-label"
                  label="Temperature"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value as '' | EnquiryTemperature)}
                >
                  <MenuItem value="">
                    <em>Not set</em>
                  </MenuItem>
                  {(Object.keys(TEMPERATURE_LABELS) as EnquiryTemperature[]).map((t) => (
                    <MenuItem key={t} value={t}>
                      {TEMPERATURE_LABELS[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={temperatureEnabled ? 6 : 12}>
            <EnquiryOptionSelect kind="SOURCE" label="Source" value={sourceId} onChange={setSourceId} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Availability"
              fullWidth
              size="small"
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />}
              label="The family asked to receive the newsletter"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid || update.isPending}>
          {update.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
